import React, { useEffect, useRef, useState } from 'react';
import { Button, Flex, Input, Modal } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import SText from '@/styles/SText';
import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from '@/styles/STableLayout';
import { Product } from '@/types/product';
import { locRefProdList, locRefProds } from '@/apis/inventoryv2';
import { CommonListParams } from '@/types/common';
import { AnyObject } from 'antd/es/_util/type';
import useDataList from '@/hooks/useDataList';
import ProductSelectMulti from '@/components/Selector/ProductSelectMulti';
import { notification } from 'antd';

interface LocationRefProductsProps {
  locationId: string;
  open: boolean;
  onClose: () => void;
}

const LocationRefProducts: React.FC<LocationRefProductsProps> = ({
  locationId,
  open,
  onClose
}) => {
  const { t } = useTranslation();
  const tableRef = useRef<TableLayoutRef | null>(null);
  const [keyword, setKeyword] = useState<string>('');
  const [selectProductOpen, setSelectProductOpen] = useState(false);

  const [list, listTotal, listLoading, { fetchList, addItems }] = useDataList<Product, [CommonListParams]>({
    fetchApi: (params) => locRefProdList(locationId, params)
  });

  const columns: ColumnsType<Product> = [
    {
      title: <SText text={t('product.code')} />,
      dataIndex: 'code',
      key: 'productCode',
      width: 150,
      render: (text: string) => <SText text={text || ''} style={{ width: '100%' }} />
    },
    {
      title: <SText text={t('product.name')} />,
      dataIndex: 'name',
      key: 'productName',
      width: 200,
      render: (text: string) => <SText text={text || ''} style={{ width: '100%' }} />
    },
    {
      title: <SText text={t('product.brand1')} />,
      dataIndex: 'model',
      key: 'model',
      width: 150,
      render: (text: string) => <SText text={text || ''} style={{ width: '100%' }} />
    },
    {
      title: <SText text={t('product.uom')} />,
      dataIndex: 'uom',
      key: 'uom',
      width: 80,
      render: (text: string) => <SText text={text || ''} style={{ width: '100%' }} />
    },
    // {
    //   title: t('common.desc'),
    //   dataIndex: 'desc',
    //   key: 'desc',
    //   width: 200,
    //   render: (text: string) => <SText text={text || ''} />
    // },
  ];

  useEffect(() => {
    if (open && locationId) {
      tableRef?.current?.resetTablePage();
      fetch(0);
    }
  }, [open, locationId]);

  const fetch = (offset: number) => {
    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;
    fetchList({ pagination: { offset, take }, body: keyword });
  };

  const handleSearch = () => {
    tableRef?.current?.resetTablePage();
    fetch(0);
  };

  const handleChange = (offset: number) => {
    fetch(offset);
  };

  const handleSelectProducts = async (products: Array<Product>) => {
    const proIds = products.map(p => p.id);
    try {
      await locRefProds(locationId, proIds);
      addItems(products);
      notification.success({ message: t('common.addSuccess') })
    } catch (err: any) {
      if (err === 70864) {
        notification.error({
          message: t('warehouse.productRef'),
          description: t('warehouse.productExists'),
        });
      }
    }
  };

  const headerChildren = (
    <Flex gap={10} align='center'>
      <Input
        style={{ maxWidth: 180 }}
        placeholder={t('warehouse.searchMaterialPlaceholder')}
        value={keyword}
        onChange={(e) => setKeyword(e.target.value.trim())}
        onPressEnter={handleSearch}
      />
      <Button
        type="primary"
        icon={<SearchOutlined />}
        onClick={handleSearch}
      />
      <Button
        type="primary"
        size="small"
        onClick={() => setSelectProductOpen(true)}
      >
        {t('counting.addItem')}
      </Button>
    </Flex>
  );

  return (
    <>
      <Modal
        title={t('warehouse.productRef')}
        open={open}
        onCancel={onClose}
        footer={null}
        width={1000}
        destroyOnClose
      >
        <Flex vertical style={{ width: "100%", height: '70vh' }}>
          <STableLayout
            headerChildren={headerChildren}
            tableLayoutRef={tableRef}
            total={listTotal}
            dataSource={list}
            handleChange={handleChange}
            columns={columns as ColumnsType<AnyObject>}
            loading={listLoading}
            scrollX={800}
            pagination={{ pageSize: 10 }}
          />

        </Flex>
      </Modal>
      <ProductSelectMulti
        openProduct={selectProductOpen}
        setOpenProduct={setSelectProductOpen}
        handleOk={handleSelectProducts}
      />
    </>
  );
};

export default LocationRefProducts;
